// Performance monitoring utility for Core Web Vitals and other metrics

interface PerformanceMetrics {
  lcp?: number
  fcp?: number
  fid?: number
  cls?: number
  ttfb?: number
  totalPageLoad?: number
  domInteractive?: number
  resourcesLoaded?: number
}

// Track Core Web Vitals
export function initPerformanceMonitoring(): void {
  // Only run in browser environment
  if (typeof window === 'undefined') return

  const metrics: PerformanceMetrics = {}

  // Largest Contentful Paint (LCP)
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lastEntry = entries[entries.length - 1] as any
      metrics.lcp = lastEntry.startTime
      logMetric('LCP', lastEntry.startTime)
      sendToAnalytics('lcp', lastEntry.startTime)
    }).observe({ entryTypes: ['largest-contentful-paint'] })
  } catch (error) {
    console.warn('LCP monitoring not supported:', error)
  }

  // First Contentful Paint (FCP)
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        if (entry.name === 'first-contentful-paint') {
          metrics.fcp = entry.startTime
          logMetric('FCP', entry.startTime)
          sendToAnalytics('fcp', entry.startTime)
        }
      })
    }).observe({ entryTypes: ['paint'] })
  } catch (error) {
    console.warn('FCP monitoring not supported:', error)
  }

  // First Input Delay (FID)
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        metrics.fid = entry.processingStart - entry.startTime
        logMetric('FID', metrics.fid)
        sendToAnalytics('fid', metrics.fid)
      })
    }).observe({ entryTypes: ['first-input'] })
  } catch (error) {
    console.warn('FID monitoring not supported:', error)
  }

  // Cumulative Layout Shift (CLS)
  try {
    let clsValue = 0
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value
        }
      })
      metrics.cls = clsValue
      logMetric('CLS', clsValue)
      sendToAnalytics('cls', clsValue)
    }).observe({ entryTypes: ['layout-shift'] })
  } catch (error) {
    console.warn('CLS monitoring not supported:', error)
  }

  // Time to First Byte (TTFB)
  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    if (navigation) {
      metrics.ttfb = navigation.responseStart - navigation.fetchStart
      logMetric('TTFB', metrics.ttfb)
      sendToAnalytics('ttfb', metrics.ttfb)
    }
  } catch (error) {
    console.warn('TTFB monitoring not supported:', error)
  }

  // Additional performance metrics
  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    if (navigation) {
      // Total page load time
      metrics.totalPageLoad = navigation.loadEventEnd - navigation.fetchStart
      
      // DOM Interactive time
      metrics.domInteractive = navigation.domInteractive - navigation.fetchStart
      
      // Number of resources loaded
      metrics.resourcesLoaded = performance.getEntriesByType('resource').length
    }
  } catch (error) {
    console.warn('Additional metrics monitoring not supported:', error)
  }

  // Log summary after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      logPerformanceSummary(metrics)
    }, 1000)
  })
}

function logMetric(name: string, value: number): void {
  if (import.meta.env.DEV) {
    console.log(`🚀 Performance [${name}]:`, `${Math.round(value)}ms`)
  }
}

function logPerformanceSummary(metrics: PerformanceMetrics): void {
  if (import.meta.env.DEV) {
    console.group('📊 Performance Summary')
    console.log('LCP (Largest Contentful Paint):', metrics.lcp ? `${Math.round(metrics.lcp)}ms` : 'N/A')
    console.log('FCP (First Contentful Paint):', metrics.fcp ? `${Math.round(metrics.fcp)}ms` : 'N/A')
    console.log('FID (First Input Delay):', metrics.fid ? `${Math.round(metrics.fid)}ms` : 'N/A')
    console.log('CLS (Cumulative Layout Shift):', metrics.cls ? metrics.cls.toFixed(3) : 'N/A')
    console.log('TTFB (Time to First Byte):', metrics.ttfb ? `${Math.round(metrics.ttfb)}ms` : 'N/A')
    console.log('Total Page Load:', metrics.totalPageLoad ? `${Math.round(metrics.totalPageLoad)}ms` : 'N/A')
    console.log('DOM Interactive:', metrics.domInteractive ? `${Math.round(metrics.domInteractive)}ms` : 'N/A')
    console.log('Resources Loaded:', metrics.resourcesLoaded || 'N/A')
    console.groupEnd()
  }
}

function sendToAnalytics(metric: string, value: number): void {
  // Send to Google Analytics if available
  if (window.gtag) {
    try {
      window.gtag('event', 'performance_metric', {
        metric_name: metric,
        metric_value: Math.round(value),
        custom_parameter: 'core_web_vitals'
      })
    } catch (error) {
      console.warn('Failed to send performance metric to Analytics:', error)
    }
  }
}